import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Badge, ProgressBar, Alert } from 'react-bootstrap';
import {
  getEnvelope,
  getEnvelopeCertificatePdf,
  getEnvelopeSignedDocumentPdf,
  getDocumentFileBatchUrls,
  revokeEnvelope,
  copyEnvelope,
  resendEnvelopeRecipient,
  sendCompletedEnvelopeToRecipient,
} from '../../services/envelopeService';
import { formatDateTimeUTC } from '../../utils/dateFormat';
import { isRevokedEnvelopeBlockedError } from '../../utils/subscription';

const EnvelopeProgress = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [envelope, setEnvelope] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [certificateLoading, setCertificateLoading] = useState(false);
  const [signedDocLoadingId, setSignedDocLoadingId] = useState(null);
  const [previewDocLoadingId, setPreviewDocLoadingId] = useState(null);
  const [revokeLoading, setRevokeLoading] = useState(false);
  const [copyLoading, setCopyLoading] = useState(false);
  const [resendingRecipientId, setResendingRecipientId] = useState(null);
  const [resendFeedback, setResendFeedback] = useState(null);
  const [completedSendLoadingKey, setCompletedSendLoadingKey] = useState('');
  const [completedSendFeedback, setCompletedSendFeedback] = useState(null);
  const previewUrlCacheRef = useRef(new Map());

  const loadEnvelope = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await getEnvelope(id);
      setEnvelope(response?.data || response);
    } catch (err) {
      console.error('Error loading envelope:', err);
      setError(err.response?.data?.message || 'Failed to load envelope details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadEnvelope();
  }, [loadEnvelope]);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'sent':
        return <Badge bg="info">Sent</Badge>;
      case 'completed':
        return <Badge bg="success">Completed</Badge>;
      case 'revoked':
        return <Badge bg="warning">Revoked</Badge>;
      case 'draft':
      default:
        return <Badge bg="primary">Draft</Badge>;
    }
  };

  const calculateProgress = () => {
    if (!envelope) return 0;
    
    let steps = 0;
    let completed = 0;

    // Step 1: Documents
    steps++;
    if (envelope.documents && envelope.documents.length > 0) {
      completed++;
    }

    // Step 2: Recipients
    steps++;
    if (envelope.recipients && envelope.recipients.length > 0) {
      completed++;
    }

    // Step 3: Fields
    steps++;
    if (envelope.fields && envelope.fields.length > 0) {
      completed++;
    }

    // Step 4: Message/Subject
    steps++;
    if (envelope.subject || envelope.message) {
      completed++;
    }

    return Math.round((completed / steps) * 100);
  };

  const handleEdit = () => {
    // Navigate to create-envelope with envelope ID as route parameter
    navigate(`/create-envelope/${id}`);
  };

  const formatDateTime = (value) => {
    return formatDateTimeUTC(value);
  };

  const openUrlInNewTab = (url) => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleOpenCertificate = async () => {
    if (certificateLoading) return;
    setCertificateLoading(true);
    const completedDetails = envelope?.completed_details;
    const certificateUrl = completedDetails?.certificate?.view_url || envelope?.certificate_url;
    const isApiStyleCertificateUrl = typeof certificateUrl === 'string' && (
      certificateUrl.includes('/api/') ||
      /api\.volksign\.com/i.test(certificateUrl)
    );

    if (certificateUrl && !isApiStyleCertificateUrl) {
      openUrlInNewTab(certificateUrl);
      setCertificateLoading(false);
      return;
    }

    try {
      const pdfBytes = await getEnvelopeCertificatePdf(id);
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      openUrlInNewTab(url);
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 422) {
        setError('Certificate is not available because this envelope is not completed yet.');
      } else if (status === 404) {
        setError('Certificate not found or access denied.');
      } else if (status === 500) {
        setError('Certificate generation failed on server. Please try again later.');
      } else {
        setError('Unable to open certificate right now.');
      }
    } finally {
      setCertificateLoading(false);
    }
  };

  const getEnvelopeDocumentIdFromSignedDoc = (doc) => {
    if (!doc) return null;
    if (doc.envelope_document_id != null) return doc.envelope_document_id;
    if (doc.document_id != null) return doc.document_id;
    if (doc.id != null) return doc.id;
    if (typeof doc.signed_document_url === 'string') {
      const match = doc.signed_document_url.match(/\/signed-document\/(\d+)/);
      if (match && match[1]) return match[1];
    }
    return null;
  };

  const handleOpenSignedDocument = async (doc) => {
    const envelopeDocumentId = getEnvelopeDocumentIdFromSignedDoc(doc);
    if (!envelopeDocumentId) {
      setError('Signed document is not available yet.');
      return;
    }

    if (signedDocLoadingId === envelopeDocumentId) return;
    setSignedDocLoadingId(envelopeDocumentId);

    try {
      const pdfBytes = await getEnvelopeSignedDocumentPdf(id, envelopeDocumentId);
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      openUrlInNewTab(url);
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 422) {
        setError('Signed document is not available because envelope is not completed yet.');
      } else if (status === 404) {
        setError('Signed document not found or access denied.');
      } else if (status === 500) {
        setError('Failed to generate signed document. Please try again later.');
      } else {
        setError('Unable to open signed document right now.');
      }
    } finally {
      setSignedDocLoadingId(null);
    }
  };

  const getOriginalDocumentId = (doc) => {
    if (!doc) return null;
    return doc.original_document_id || doc.document_id || doc.id || null;
  };

  const handlePreviewEnvelopeDocument = async (doc) => {
    const documentId = getOriginalDocumentId(doc);
    if (!documentId) {
      setError('Document preview is not available.');
      return;
    }

    if (previewDocLoadingId === documentId) return;
    setPreviewDocLoadingId(documentId);
    try {
      const numericDocumentId = typeof documentId === 'string' ? parseInt(documentId, 10) : documentId;
      const cacheKey = Number.isFinite(numericDocumentId) ? numericDocumentId : documentId;
      let previewUrl = previewUrlCacheRef.current.get(cacheKey) || '';

      // Prefer existing non-legacy URL if API already provided one.
      const rawDocUrl = String(doc.document_url || '').trim();
      if (!previewUrl && rawDocUrl && !/\/document-file\/\d+/.test(rawDocUrl)) {
        previewUrl = rawDocUrl;
      }

      if (!previewUrl && Number.isFinite(numericDocumentId)) {
        const batchResponse = await getDocumentFileBatchUrls([numericDocumentId], 15);
        const rows = Array.isArray(batchResponse?.items) ? batchResponse.items : [];
        const row = rows.find((item) => {
          const rowId = typeof item?.document_id === 'string' ? parseInt(item.document_id, 10) : item?.document_id;
          return rowId === numericDocumentId;
        });
        previewUrl = row?.temporary_url || '';
      }

      if (!previewUrl) {
        throw new Error('Unable to generate temporary document URL.');
      }

      previewUrlCacheRef.current.set(cacheKey, previewUrl);
      openUrlInNewTab(previewUrl);
    } catch (err) {
      setError(err?.response?.data?.message || 'Unable to preview document right now.');
    } finally {
      setPreviewDocLoadingId(null);
    }
  };

  const handleRevokeEnvelope = async () => {
    if (revokeLoading || !id) return;
    if (!window.confirm('Are you sure you want to revoke this envelope?')) return;
    try {
      setRevokeLoading(true);
      await revokeEnvelope(id);
      await loadEnvelope();
    } catch (err) {
      console.error('Error revoking envelope:', err);
      setError(err?.response?.data?.message || 'Failed to revoke envelope.');
    } finally {
      setRevokeLoading(false);
    }
  };

  const getCopiedEnvelopeId = (response) => {
    const data = response?.data || response || {};
    return (
      data.envelope_id ||
      data.id ||
      data.envelope?.id ||
      data.envelope?.envelope_id ||
      data.data?.envelope_id ||
      data.data?.id ||
      data.data?.envelope?.id ||
      data.data?.envelope?.envelope_id ||
      null
    );
  };

  const handleCopyEnvelope = async () => {
    if (copyLoading || !id) return;
    try {
      setCopyLoading(true);
      setError('');
      const response = await copyEnvelope(id);
      const copiedEnvelopeId = getCopiedEnvelopeId(response);
      if (!copiedEnvelopeId) {
        throw new Error('Envelope copied, but no new envelope ID was returned.');
      }
      navigate(`/create-envelope/${copiedEnvelopeId}`);
    } catch (err) {
      console.error('Error copying envelope:', err);
      setError(err?.response?.data?.message || err?.message || 'Failed to copy envelope.');
    } finally {
      setCopyLoading(false);
    }
  };

  const getRecipientApiId = (recipient) => {
    if (!recipient) return null;
    return recipient.id ?? recipient.envelope_recipient_id ?? recipient.recipient_id ?? null;
  };

  const getRecipientSignatureOrder = (recipient) => {
    const raw =
      recipient?.signature_order ??
      recipient?.signing_order ??
      recipient?.order ??
      recipient?.signatureOrder;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const sortRecipientsBySignatureOrder = (list) =>
    [...(list || [])].sort((a, b) => {
      const oa = getRecipientSignatureOrder(a);
      const ob = getRecipientSignatureOrder(b);
      if (oa == null && ob == null) return 0;
      if (oa == null) return 1;
      if (ob == null) return -1;
      return oa - ob;
    });

  const isSequentialWorkflow = (workflowType) =>
    String(workflowType || '').toLowerCase() === 'sequential';

  const renderSigningOrderBadge = (recipient, { className = 'me-2 flex-shrink-0' } = {}) => {
    const order = getRecipientSignatureOrder(recipient);
    if (order == null) return null;
    return (
      <div
        className={`rounded-circle ${className}`}
        style={{
          width: '24px',
          height: '24px',
          backgroundColor: recipient.signee_color || '#6366f1',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: '11px',
          fontWeight: 'bold',
        }}
        aria-label={`Signing order ${order}`}
        title={`Signing order ${order}`}
      >
        {order}
      </div>
    );
  };

  const renderRecipientInitialBadge = (recipient) => (
    <div
      className="rounded-circle me-2 flex-shrink-0"
      style={{
        width: '24px',
        height: '24px',
        backgroundColor: recipient.signee_color || '#6366f1',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontSize: '10px',
        fontWeight: 'bold',
      }}
    >
      {recipient.name ? recipient.name.charAt(0).toUpperCase() : '?'}
    </div>
  );

  const renderRecipientPrimaryRow = (
    recipient,
    workflowType,
    { name, email, extra = null, useInitialWhenNotSequential = false }
  ) => (
    <div className="d-flex align-items-start gap-2">
      {isSequentialWorkflow(workflowType)
        ? renderSigningOrderBadge(recipient)
        : useInitialWhenNotSequential
          ? renderRecipientInitialBadge(recipient)
          : null}
      <div>
        <div className="fw-semibold">{name}</div>
        {email != null && email !== '' && <div className="text-muted small">{email}</div>}
        {extra}
      </div>
    </div>
  );

  const canResendRecipient = (recipient, status, workflowType, currentSignatureOrder) => {
    const recipientApiId = getRecipientApiId(recipient);
    if (!recipientApiId) {
      return { allowed: false, reason: 'Recipient ID is unavailable for resend.' };
    }
    if ((envelope?.status || '').toLowerCase() === 'revoked') {
      return {
        allowed: false,
        reason: 'Revoked envelopes cannot be resent. Copy this envelope to create a new draft.',
      };
    }
    if (status === 'completed') {
      return { allowed: false, reason: 'Email is not sent because this recipient has already signed.' };
    }

    const isSequential = String(workflowType || '').toLowerCase() === 'sequential';
    const recipientOrder = getRecipientSignatureOrder(recipient);
    const currentOrderNum = Number(currentSignatureOrder);
    if (isSequential && status !== 'active') {
      if (
        Number.isFinite(recipientOrder)
        && Number.isFinite(currentOrderNum)
        && recipientOrder > currentOrderNum
      ) {
        return { allowed: false, reason: 'Email is not sent because this recipient turn has not started yet.' };
      }
      return { allowed: false, reason: 'Email is not sent because this recipient turn has not started yet.' };
    }

    return { allowed: true, reason: '' };
  };

  const handleResendRecipient = async (recipient, status, workflowType, currentSignatureOrder) => {
    const recipientApiId = getRecipientApiId(recipient);
    const recipientLabel =
      recipient?.name ||
      recipient?.resolved_name ||
      recipient?.email ||
      recipient?.resolved_email ||
      recipient?.signee_placeholder ||
      recipient?.placeholder ||
      'Recipient';
    const eligibility = canResendRecipient(recipient, status, workflowType, currentSignatureOrder);
    if (!eligibility.allowed) {
      setResendFeedback({ type: 'warning', message: eligibility.reason });
      return;
    }
    if (!recipientApiId || resendingRecipientId === recipientApiId) return;

    try {
      setResendingRecipientId(recipientApiId);
      setResendFeedback(null);
      const response = await resendEnvelopeRecipient(recipientApiId);
      setResendFeedback({
        type: 'success',
        message: response?.message || `Resend email sent to ${recipientLabel}.`,
      });
    } catch (err) {
      if (isRevokedEnvelopeBlockedError(err)) {
        setResendFeedback({
          type: 'warning',
          message:
            err?.response?.data?.message
            || 'Revoked envelopes cannot be edited or resent. Copy this envelope to create a new draft, then send the copy.',
        });
      } else {
        setResendFeedback({
          type: 'danger',
          message: err?.response?.data?.message || err?.message || 'Failed to resend email.',
        });
      }
    } finally {
      setResendingRecipientId(null);
    }
  };

  const handleSendCompletedRecipient = async (recipient, sendType) => {
    const recipientApiId = getRecipientApiId(recipient);
    const recipientLabel =
      recipient?.name ||
      recipient?.resolved_name ||
      recipient?.email ||
      recipient?.resolved_email ||
      recipient?.signee_placeholder ||
      recipient?.placeholder ||
      'Recipient';
    if (!recipientApiId) {
      setCompletedSendFeedback({
        type: 'warning',
        message: 'Recipient ID is unavailable for sending completed envelope emails.',
      });
      return;
    }

    const normalizedSendType = String(sendType || '').trim().toLowerCase();
    const loadingKey = `${recipientApiId}:${normalizedSendType}`;
    if (completedSendLoadingKey === loadingKey) return;

    try {
      setCompletedSendLoadingKey(loadingKey);
      setCompletedSendFeedback(null);
      const response = await sendCompletedEnvelopeToRecipient(recipientApiId, normalizedSendType);
      const successLabel = normalizedSendType === 'certificate' ? 'Certificate' : 'Signed documents';
      setCompletedSendFeedback({
        type: 'success',
        message: response?.message || `${successLabel} email sent to ${recipientLabel}.`,
      });
    } catch (err) {
      setCompletedSendFeedback({
        type: 'danger',
        message: err?.response?.data?.message || err?.message || 'Failed to send completed envelope email.',
      });
    } finally {
      setCompletedSendLoadingKey('');
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="envelope-progress-page">
        <Alert variant="danger">
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
          <Button variant="primary" onClick={() => navigate(-1)}>
            Back
          </Button>
        </Alert>
      </div>
    );
  }

  if (!envelope) {
    return (
      <div className="envelope-progress-page">
        <Alert variant="warning">
          <Alert.Heading>Envelope Not Found</Alert.Heading>
          <p>The envelope you're looking for doesn't exist.</p>
          <Button variant="primary" onClick={() => navigate(-1)}>
            Back
          </Button>
        </Alert>
      </div>
    );
  }

  const progress = calculateProgress();
  const isCompleted = (envelope?.status || '').toLowerCase() === 'completed';
  const isSent = (envelope?.status || '').toLowerCase() === 'sent';
  const isRevoked = (envelope?.status || '').toLowerCase() === 'revoked';
  const completedDetails = envelope?.completed_details || null;
  const signingProgress = envelope?.progress || null;
  const draftFields = Array.isArray(envelope?.fields) ? envelope.fields : [];
  const draftRecipients = Array.isArray(envelope?.recipients) ? envelope.recipients : [];

  const getRecipientIdentityKey = (r) => {
    if (!r) return '';
    const id = r.id ?? r.envelope_recipient_id ?? r.recipient_id ?? null;
    if (id != null) return `id:${id}`;
    const email = r.email || r.resolved_email || null;
    if (email) return `email:${String(email).trim().toLowerCase()}`;
    const placeholder = r.signee_placeholder || r.placeholder || null;
    if (placeholder) return `placeholder:${String(placeholder).trim().toLowerCase()}`;
    return '';
  };

  const recipientLookup = (() => {
    const map = new Map();
    draftRecipients.forEach((recipient) => {
      const key = getRecipientIdentityKey(recipient);
      if (key) map.set(key, recipient);
    });
    return map;
  })();

  const normalizeRecipientRef = (value) => {
    if (value == null || value === '') return '';
    const raw = String(value).trim();
    if (!raw) return '';
    if (raw.includes(':')) {
      const [prefix, ...rest] = raw.split(':');
      const payload = rest.join(':').trim();
      if (!payload) return '';
      if (prefix === 'email') return `email:${payload.toLowerCase()}`;
      if (prefix === 'placeholder') return `placeholder:${payload.toLowerCase()}`;
      if (prefix === 'id') return `id:${payload}`;
      return `${prefix}:${payload}`;
    }
    if (raw.includes('@')) return `email:${raw.toLowerCase()}`;
    return `id:${raw}`;
  };

  const fieldTypeBreakdown = (() => {
    const counts = {};
    draftFields.forEach((field) => {
      const type = String(field?.field_type || field?.type || 'unknown').trim().toLowerCase() || 'unknown';
      counts[type] = (counts[type] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  })();

  const fieldRecipientBreakdown = (() => {
    const counts = new Map();
    draftFields.forEach((field) => {
      const candidateRefs = [
        normalizeRecipientRef(field?.recipient_id),
        normalizeRecipientRef(field?.recipient_email),
        normalizeRecipientRef(field?.recipient?.id),
        normalizeRecipientRef(field?.recipient?.email),
        normalizeRecipientRef(field?.recipient?.signee_placeholder || field?.recipient?.placeholder),
      ].filter(Boolean);

      const key = candidateRefs.find((k) => recipientLookup.has(k)) || candidateRefs[0] || 'unassigned';
      counts.set(key, (counts.get(key) || 0) + 1);
    });

    return Array.from(counts.entries())
      .map(([key, count]) => {
        const recipient = key === 'unassigned' ? null : recipientLookup.get(key) || null;
        const name =
          recipient?.name ||
          recipient?.resolved_name ||
          recipient?.email ||
          recipient?.resolved_email ||
          recipient?.signee_placeholder ||
          recipient?.placeholder ||
          (key.startsWith('email:') ? key.replace('email:', '') : key.startsWith('placeholder:') ? key.replace('placeholder:', '') : key === 'unassigned' ? 'Unassigned' : key.replace('id:', `Recipient #`));
        const email = recipient?.email || recipient?.resolved_email || (key.startsWith('email:') ? key.replace('email:', '') : '');
        const placeholder = recipient?.signee_placeholder || recipient?.placeholder || (key.startsWith('placeholder:') ? key.replace('placeholder:', '') : '');
        return { key, count, name, email, placeholder };
      })
      .sort((a, b) => b.count - a.count || String(a.name).localeCompare(String(b.name)));
  })();

  if (isCompleted) {
    if (!completedDetails) {
      return (
        <div className="envelope-progress-page">
          <Alert variant="warning" className="mb-4">
            <Alert.Heading>Completed Details Unavailable</Alert.Heading>
            <p className="mb-0">
              This envelope is marked as completed, but detailed completion data is not available.
            </p>
          </Alert>
          <div className="d-flex gap-2 justify-content-end">
            <Button variant="outline-secondary" onClick={() => navigate('/envelopes/completed')}>
              <i className="ri-arrow-left-line me-2"></i>
              Back to Completed
            </Button>
            <Button variant="primary" onClick={handleCopyEnvelope} disabled={copyLoading}>
              {copyLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Copying...
                </>
              ) : (
                <>
                  <i className="ri-file-copy-line me-2"></i>
                  Copy Envelope
                </>
              )}
            </Button>
          </div>
        </div>
      );
    }

    const completedEnvelope = completedDetails.envelope || {};
    const apiRecipients = Array.isArray(completedDetails.recipients) ? completedDetails.recipients : [];
    const envelopeRecipients = Array.isArray(envelope.recipients) ? envelope.recipients : [];
    const seen = new Set();
    const recipients = [...apiRecipients, ...envelopeRecipients].filter((r) => {
      const id = r?.id ?? r?.envelope_recipient_id ?? r?.recipient_id ?? null;
      const email = r?.email || r?.resolved_email || null;
      const placeholder = r?.signee_placeholder || r?.placeholder || null;
      const key = id != null ? `id:${id}` : email ? `email:${email}` : placeholder ? `placeholder:${placeholder}` : '';
      if (!key) return false;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    const signedDocs = Array.isArray(completedDetails.signed_documents_emailed_with_certificate)
      ? completedDetails.signed_documents_emailed_with_certificate
      : [];
    const completedWorkflowType =
      completedEnvelope.workflow_type || envelope?.workflow_type || 'parallel';
    const completedRecipientsForList = isSequentialWorkflow(completedWorkflowType)
      ? sortRecipientsBySignatureOrder(recipients)
      : recipients;

    return (
      <div className="envelope-progress-page">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="mb-1">{completedEnvelope.subject || envelope.subject || envelope.name || 'Completed Envelope'}</h1>
            <div className="d-flex align-items-center gap-2">
              <Badge bg="success">Completed</Badge>
              <span className="text-muted">Completed: {formatDateTime(completedEnvelope.completed_at)}</span>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Button variant="outline-primary" onClick={handleOpenCertificate} disabled={certificateLoading}>
              {certificateLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Loading Certificate...
                </>
              ) : (
                <>
                  <i className="ri-award-line me-2"></i>
                  View Certificate
                </>
              )}
            </Button>
            <Button variant="primary" onClick={handleCopyEnvelope} disabled={copyLoading}>
              {copyLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Copying...
                </>
              ) : (
                <>
                  <i className="ri-file-copy-line me-2"></i>
                  Copy Envelope
                </>
              )}
            </Button>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6 mb-4">
            <Card>
              <Card.Header>
                <h6 className="mb-0">
                  <i className="ri-mail-line me-2"></i>
                  Envelope Details
                </h6>
              </Card.Header>
              <Card.Body>
                <div className="mb-3">
                  <strong>Subject:</strong>
                  <div className="text-muted">{completedEnvelope.subject || 'N/A'}</div>
                </div>
                <div className="mb-3">
                  <strong>Message:</strong>
                  <div className="text-muted">{completedEnvelope.message || 'N/A'}</div>
                </div>
                <div className="mb-2">
                  <strong>Sent At:</strong> <span className="text-muted">{formatDateTime(completedEnvelope.sent_at)}</span>
                </div>
                <div>
                  <strong>Completed At:</strong> <span className="text-muted">{formatDateTime(completedEnvelope.completed_at)}</span>
                </div>
              </Card.Body>
            </Card>
          </div>

          <div className="col-md-6 mb-4">
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0">
                  <i className="ri-user-line me-2"></i>
                  Recipients
                </h6>
                <Badge bg="success">{recipients.length}</Badge>
              </Card.Header>
              <Card.Body>
                {completedSendFeedback && (
                  <Alert
                    variant={completedSendFeedback.type}
                    className="mb-3 py-2"
                    dismissible
                    onClose={() => setCompletedSendFeedback(null)}
                  >
                    {completedSendFeedback.message}
                  </Alert>
                )}
                {completedRecipientsForList.length > 0 ? (
                  <ul className="list-unstyled mb-0">
                    {completedRecipientsForList.map((recipient, index) => {
                      const recipientApiId = getRecipientApiId(recipient);
                      const certLoading = completedSendLoadingKey === `${recipientApiId}:certificate`;
                      const docsLoading = completedSendLoadingKey === `${recipientApiId}:documents`;
                      const disableRow = !recipientApiId || Boolean(completedSendLoadingKey);
                      return (
                        <li key={recipient.id || index} className="mb-2">
                          <div className="d-flex align-items-center justify-content-between gap-3">
                            <div>
                              {renderRecipientPrimaryRow(recipient, completedWorkflowType, {
                                name: recipient.name || recipient.resolved_name || 'Unknown',
                                email: recipient.email || 'N/A',
                              })}
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                disabled={disableRow}
                                onClick={() => handleSendCompletedRecipient(recipient, 'certificate')}
                                title={!recipientApiId ? 'Recipient ID unavailable' : 'Send only certificate'}
                              >
                                {certLoading ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                    Sending...
                                  </>
                                ) : (
                                  <>Send Certificate</>
                                )}
                              </Button>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                disabled={disableRow}
                                onClick={() => handleSendCompletedRecipient(recipient, 'documents')}
                                title={!recipientApiId ? 'Recipient ID unavailable' : 'Send signed documents'}
                              >
                                {docsLoading ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                    Sending...
                                  </>
                                ) : (
                                  <>Send Documents</>
                                )}
                              </Button>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="text-muted mb-0">No recipient details available.</p>
                )}
              </Card.Body>
            </Card>
          </div>

          <div className="col-12 mb-4">
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0">
                  <i className="ri-file-list-3-line me-2"></i>
                  Signed Documents Emailed With Certificate
                </h6>
                <Badge bg="secondary">{signedDocs.length}</Badge>
              </Card.Header>
              <Card.Body>
                {signedDocs.length > 0 ? (
                  <ul className="list-unstyled mb-0">
                    {signedDocs.map((doc, index) => (
                      <li key={doc.id || index} className="d-flex justify-content-between align-items-center py-2 border-bottom">
                        <span>{doc.title || doc.document_title || `Document ${index + 1}`}</span>
                        {doc.signed_document_url ? (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleOpenSignedDocument(doc)}
                            disabled={signedDocLoadingId === getEnvelopeDocumentIdFromSignedDoc(doc)}
                          >
                            {signedDocLoadingId === getEnvelopeDocumentIdFromSignedDoc(doc) ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Loading...
                              </>
                            ) : (
                              <>
                                <i className="ri-external-link-line me-1"></i>
                                Open
                              </>
                            )}
                          </Button>
                        ) : (
                          <span className="text-muted small">Signed copy not available yet</span>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-muted mb-0">No signed document entries available.</p>
                )}
              </Card.Body>
            </Card>
          </div>
        </div>

        <div className="d-flex gap-2 justify-content-end mt-4">
          <Button variant="outline-secondary" onClick={() => navigate('/envelopes/completed')}>
            <i className="ri-arrow-left-line me-2"></i>
            Back to Completed
          </Button>
          <Button variant="primary" onClick={handleCopyEnvelope} disabled={copyLoading}>
            {copyLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Copying...
              </>
            ) : (
              <>
                <i className="ri-file-copy-line me-2"></i>
                Copy Envelope
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }

  if (isSent && !isCompleted) {
    const p = signingProgress || {};
    const totalSignees = Number(p.total_signees || 0);
    const signedSignees = Number(p.signed_signees || 0);
    const declinedSignees = Number(p.declined_signees || 0);
    const pendingSignees = Number(p.pending_signees || 0);
    const progressPercent = Number(p.progress_percent || 0);
    const workflowType = p.workflow_type || envelope?.workflow_type || 'parallel';
    const currentOrder = p.current_signature_order ?? 'N/A';
    const activeSignees = Array.isArray(p.active_signees) ? p.active_signees : [];
    const completedSignees = Array.isArray(p.completed_signees) ? p.completed_signees : [];
    const sentDocuments = Array.isArray(envelope.documents) ? envelope.documents : [];

    const getRecipientIdentityKey = (r) => {
      if (!r) return '';
      const id = r.id ?? r.envelope_recipient_id ?? r.recipient_id ?? null;
      if (id != null) return `id:${id}`;
      const email = r.email || r.resolved_email || null;
      if (email) return `email:${email}`;
      const placeholder = r.signee_placeholder || r.placeholder || null;
      if (placeholder) return `placeholder:${placeholder}`;
      return '';
    };

    const recipientStatusSets = {
      active: new Set(activeSignees.map(getRecipientIdentityKey).filter(Boolean)),
      completed: new Set(completedSignees.map(getRecipientIdentityKey).filter(Boolean)),
      declined: new Set((Array.isArray(p.declined_signees) ? p.declined_signees : []).map(getRecipientIdentityKey).filter(Boolean)),
      pending: new Set((Array.isArray(p.pending_signees) ? p.pending_signees : []).map(getRecipientIdentityKey).filter(Boolean)),
    };

    const allRecipients = Array.isArray(envelope.recipients) ? envelope.recipients : [];
    const recipientsForList = isSequentialWorkflow(workflowType)
      ? sortRecipientsBySignatureOrder(allRecipients)
      : allRecipients;
    const getRecipientLabel = (r) =>
      r?.name || r?.resolved_name || r?.email || r?.resolved_email || r?.signee_placeholder || r?.placeholder || 'Unknown';

    const mergeSigneeWithEnvelopeRecipient = (signee) => {
      const key = getRecipientIdentityKey(signee);
      if (!key) return signee;
      const match = allRecipients.find((r) => getRecipientIdentityKey(r) === key);
      if (!match) return signee;
      return {
        ...signee,
        signee_color: signee.signee_color || match.signee_color,
        signature_order: signee.signature_order ?? match.signature_order,
        signing_order: signee.signing_order ?? match.signing_order,
      };
    };

    const getProgressRecipientDetails = (recipient) => {
      const key = getRecipientIdentityKey(recipient);
      if (!key) return null;
      return activeSignees.find((r) => getRecipientIdentityKey(r) === key)
        || completedSignees.find((r) => getRecipientIdentityKey(r) === key)
        || null;
    };

    const getRecipientStatus = (r) => {
      const key = getRecipientIdentityKey(r);
      if (!key) return 'pending';
      if (recipientStatusSets.declined.has(key)) return 'declined';
      if (recipientStatusSets.completed.has(key)) return 'completed';
      if (recipientStatusSets.active.has(key)) return 'active';
      if (recipientStatusSets.pending.has(key)) return 'pending';
      return 'pending';
    };

    const getRecipientStatusBadge = (status) => {
      switch (status) {
        case 'completed':
          return <Badge bg="success">Completed</Badge>;
        case 'declined':
          return <Badge bg="warning">Declined</Badge>;
        case 'active':
          return <Badge bg="info">Active</Badge>;
        case 'pending':
        default:
          return <Badge bg="secondary">Pending</Badge>;
      }
    };

    return (
      <div className="envelope-progress-page">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="mb-1">{envelope.subject || envelope.name || 'Sent Envelope'}</h1>
            <div className="d-flex align-items-center gap-2">
              <Badge bg="info">In Progress</Badge>
              <span className="text-muted">
                Sent: {formatDateTimeUTC(envelope.sent_at)}
              </span>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Button variant="outline-primary" onClick={handleCopyEnvelope} disabled={copyLoading}>
              {copyLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Copying...
                </>
              ) : (
                <>
                  <i className="ri-file-copy-line me-2"></i>
                  Copy Envelope
                </>
              )}
            </Button>
            <Button variant="outline-danger" onClick={handleRevokeEnvelope} disabled={revokeLoading}>
            {revokeLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Revoking...
              </>
            ) : (
              <>
                <i className="ri-forbid-line me-2"></i>
                Revoke Envelope
              </>
            )}
          </Button>
          </div>
        </div>

        <Card className="mb-4">
          <Card.Body>
            <h5 className="mb-3">Signing Progress</h5>
            <ProgressBar now={progressPercent} label={`${progressPercent}%`} className="mb-2" />
            <p className="text-muted small mb-0">
              {p.is_completed ? 'Envelope completed' : p.is_in_progress ? 'Envelope is currently in progress' : 'Waiting for activity'}
            </p>
          </Card.Body>
        </Card>

        <div className="row">
          <div className="col-12 mb-4">
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0">
                  <i className="ri-user-line me-2"></i>
                  Recipients
                </h6>
                <Badge bg="secondary">{allRecipients.length}</Badge>
              </Card.Header>
              <Card.Body>
                {resendFeedback && (
                  <Alert
                    variant={resendFeedback.type}
                    className="mb-3 py-2"
                    dismissible
                    onClose={() => setResendFeedback(null)}
                  >
                    {resendFeedback.message}
                  </Alert>
                )}
                {recipientsForList.length > 0 ? (
                  <ul className="list-unstyled mb-0">
                    {recipientsForList.map((r, index) => {
                      const status = getRecipientStatus(r);
                      const progressRecipient = getProgressRecipientDetails(r);
                      const lastOpenedAt = progressRecipient?.last_opened_at || null;
                      const signedAt = progressRecipient?.signed_at || null;
                      const eligibility = canResendRecipient(r, status, workflowType, currentOrder);
                      const recipientApiId = getRecipientApiId(r);
                      const isRowResending = recipientApiId != null && resendingRecipientId === recipientApiId;
                      return (
                        <li key={r?.id ?? r?.email ?? r?.resolved_email ?? r?.signee_placeholder ?? `${index}`} className="mb-2">
                          <div className="d-flex align-items-center justify-content-between gap-3">
                            <div>
                              {renderRecipientPrimaryRow(r, workflowType, {
                                name: getRecipientLabel(r),
                                email: (r?.email || r?.resolved_email)
                                  ? (r.email || r.resolved_email)
                                  : (r?.signee_placeholder || r?.placeholder || ''),
                              })}
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              {signedAt && (
                                <span className="text-muted small">
                                  Signed: {formatDateTimeUTC(signedAt)}
                                </span>
                              )}
                              {lastOpenedAt && (
                                <span className="text-muted small">
                                  Last opened: {formatDateTimeUTC(lastOpenedAt)}
                                </span>
                              )}
                              {getRecipientStatusBadge(status)}
                              <Button
                                variant="outline-primary"
                                size="sm"
                                disabled={!eligibility.allowed || isRowResending}
                                onClick={() => handleResendRecipient(r, status, workflowType, currentOrder)}
                                title={eligibility.allowed ? 'Resend signature request email' : eligibility.reason}
                              >
                                {isRowResending ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                    Sending...
                                  </>
                                ) : (
                                  <>
                                    <i className="ri-mail-send-line me-1"></i>
                                    Resend
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="text-muted mb-0">No recipient details available.</p>
                )}
              </Card.Body>
            </Card>
          </div>

          <div className="col-md-6 mb-4">
            <Card>
              <Card.Header>
                <h6 className="mb-0">
                  <i className="ri-bar-chart-line me-2"></i>
                  Summary
                </h6>
              </Card.Header>
              <Card.Body>
                <div className="mb-2"><strong>Total Signees:</strong> <span className="text-muted">{totalSignees}</span></div>
                <div className="mb-2"><strong>Signed:</strong> <span className="text-muted">{signedSignees}</span></div>
                <div className="mb-2"><strong>Declined:</strong> <span className="text-muted">{declinedSignees}</span></div>
                <div className="mb-2"><strong>Pending:</strong> <span className="text-muted">{pendingSignees}</span></div>
                <div className="mb-2"><strong>Workflow:</strong> <span className="text-muted text-capitalize">{workflowType}</span></div>
                <div><strong>Current Signature Order:</strong> <span className="text-muted">{currentOrder}</span></div>
              </Card.Body>
            </Card>
          </div>

          <div className="col-md-6 mb-4">
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0">
                  <i className="ri-user-follow-line me-2"></i>
                  Active Signees
                </h6>
                <Badge bg="info">{activeSignees.length}</Badge>
              </Card.Header>
              <Card.Body>
                {activeSignees.length > 0 ? (
                  <ul className="list-unstyled mb-0">
                    {activeSignees.map((recipient, index) => {
                      const merged = mergeSigneeWithEnvelopeRecipient(recipient);
                      const lastOpened = merged.last_opened_at || recipient.last_opened_at;
                      return (
                        <li key={recipient.id || recipient.email || index} className="mb-3">
                          {renderRecipientPrimaryRow(merged, workflowType, {
                            name: merged.name || merged.resolved_name || 'Unknown',
                            email: merged.email || 'N/A',
                          })}
                          {lastOpened && (
                            <div className="text-muted small mt-1 ms-1">
                              <i className="ri-eye-line me-1"></i>
                              Last opened: {formatDateTimeUTC(lastOpened)}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="text-muted mb-0">No active signees right now.</p>
                )}
              </Card.Body>
            </Card>
          </div>

          <div className="col-12 mb-4">
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0">
                  <i className="ri-file-line me-2"></i>
                  Documents
                </h6>
                <Badge bg="secondary">{sentDocuments.length}</Badge>
              </Card.Header>
              <Card.Body>
                {sentDocuments.length > 0 ? (
                  <ul className="list-unstyled mb-0">
                    {sentDocuments.map((doc, index) => {
                      const docId = getOriginalDocumentId(doc);
                      const loading = previewDocLoadingId === docId;
                      return (
                        <li key={doc.id || docId || index} className="d-flex justify-content-between align-items-center py-2 border-bottom">
                          <span>{doc.title || doc.document_title || `Document ${index + 1}`}</span>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handlePreviewEnvelopeDocument(doc)}
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Loading...
                              </>
                            ) : (
                              <>
                                <i className="ri-eye-line me-1"></i>
                                Preview
                              </>
                            )}
                          </Button>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="text-muted mb-0">No documents available for this envelope.</p>
                )}
              </Card.Body>
            </Card>
          </div>

          <div className="col-12 mb-4">
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0">
                  <i className="ri-checkbox-circle-line me-2"></i>
                  Completed Signees
                </h6>
                <Badge bg="success">{completedSignees.length}</Badge>
              </Card.Header>
              <Card.Body>
                {completedSignees.length > 0 ? (
                  <ul className="list-unstyled mb-0">
                    {completedSignees.map((recipient, index) => {
                      const merged = mergeSigneeWithEnvelopeRecipient(recipient);
                      return (
                        <li key={recipient.id || recipient.email || index} className="mb-3">
                          {renderRecipientPrimaryRow(merged, workflowType, {
                            name: merged.name || merged.resolved_name || 'Unknown',
                            email: merged.email || 'N/A',
                          })}
                          {(merged.signed_at || recipient.signed_at) && (
                            <div className="text-muted small mt-1 ms-1">
                              <i className="ri-check-line me-1"></i>
                              Signed: {formatDateTimeUTC(merged.signed_at || recipient.signed_at)}
                            </div>
                          )}
                          {(merged.last_opened_at || recipient.last_opened_at) && (
                            <div className="text-muted small mt-1 ms-1">
                              <i className="ri-eye-line me-1"></i>
                              Last opened: {formatDateTimeUTC(merged.last_opened_at || recipient.last_opened_at)}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="text-muted mb-0">No completed signees yet.</p>
                )}
              </Card.Body>
            </Card>
          </div>
        </div>

        <div className="d-flex gap-2 justify-content-end mt-4">
          <Button variant="outline-secondary" onClick={() => navigate('/envelopes/sent')}>
            <i className="ri-arrow-left-line me-2"></i>
            Back to Sent
          </Button>
          <Button variant="primary" onClick={handleCopyEnvelope} disabled={copyLoading}>
            {copyLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Copying...
              </>
            ) : (
              <>
                <i className="ri-file-copy-line me-2"></i>
                Copy Envelope
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }

  const draftWorkflowType = envelope?.workflow_type || 'parallel';
  const draftRecipientsForList =
    Array.isArray(envelope?.recipients) && isSequentialWorkflow(draftWorkflowType)
      ? sortRecipientsBySignatureOrder(envelope.recipients)
      : (envelope?.recipients || []);

  return (
    <div className="envelope-progress-page">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="mb-1">{envelope.subject || envelope.name || 'Untitled Envelope'}</h1>
          <div className="d-flex align-items-center gap-2">
            {getStatusBadge(envelope.status || 'draft')}
            <span className="text-muted">
              Created: {formatDateTimeUTC(envelope.created_at)}
            </span>
          </div>
        </div>
        {isRevoked ? (
          <Button variant="primary" onClick={handleCopyEnvelope} disabled={copyLoading}>
            {copyLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Copying...
              </>
            ) : (
              <>
                <i className="ri-file-copy-line me-2"></i>
                Copy Envelope
              </>
            )}
          </Button>
        ) : (
          <Button variant="primary" onClick={handleEdit}>
            <i className="ri-edit-line me-2"></i>
            Continue Editing
          </Button>
        )}
      </div>

      {isRevoked && (
        <Alert variant="warning" className="mb-4">
          This envelope has been revoked and is view-only.
        </Alert>
      )}

      {/* Progress Bar */}
      <Card className="mb-4">
        <Card.Body>
          <h5 className="mb-3">Envelope Progress</h5>
          <ProgressBar now={progress} label={`${progress}%`} className="mb-2" />
          <p className="text-muted small mb-0">
            {isRevoked ? 'This envelope is void and cannot be edited.' : 'Complete all steps to send your envelope'}
          </p>
        </Card.Body>
      </Card>

      {/* Progress Steps */}
      <div className="row">
        <div className="col-md-6 mb-4">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0">
                <i className="ri-file-line me-2"></i>
                Documents
              </h6>
              {envelope.documents && envelope.documents.length > 0 ? (
                <Badge bg="success">
                  <i className="ri-check-line me-1"></i>
                  {envelope.documents.length} {envelope.documents.length === 1 ? 'Document' : 'Documents'}
                </Badge>
              ) : (
                <Badge bg="secondary">Not Added</Badge>
              )}
            </Card.Header>
            <Card.Body>
              {envelope.documents && envelope.documents.length > 0 ? (
                <ul className="list-unstyled mb-0">
                  {envelope.documents.map((doc, index) => {
                    const docId = getOriginalDocumentId(doc);
                    const loading = previewDocLoadingId === docId;
                    return (
                      <li key={doc.id || docId || index} className="d-flex justify-content-between align-items-center gap-3 py-2 border-bottom">
                        <span>
                          <i className="ri-file-pdf-line me-2 text-danger"></i>
                          {doc.title || doc.document_title || `Document ${index + 1}`}
                        </span>
                        {isRevoked && (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handlePreviewEnvelopeDocument(doc)}
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Loading...
                              </>
                            ) : (
                              <>
                                <i className="ri-eye-line me-1"></i>
                                Preview
                              </>
                            )}
                          </Button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="text-muted mb-0">No documents added yet</p>
              )}
            </Card.Body>
          </Card>
        </div>

        <div className="col-md-6 mb-4">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0">
                <i className="ri-user-line me-2"></i>
                Recipients
              </h6>
              {envelope.recipients && envelope.recipients.length > 0 ? (
                <Badge bg="success">
                  <i className="ri-check-line me-1"></i>
                  {envelope.recipients.length} {envelope.recipients.length === 1 ? 'Recipient' : 'Recipients'}
                </Badge>
              ) : (
                <Badge bg="secondary">Not Added</Badge>
              )}
            </Card.Header>
            <Card.Body>
              {draftRecipientsForList.length > 0 ? (
                <ul className="list-unstyled mb-0">
                  {draftRecipientsForList.map((recipient, index) => (
                    <li key={recipient.id || index} className="mb-2">
                      {renderRecipientPrimaryRow(recipient, draftWorkflowType, {
                        name: recipient.name || recipient.resolved_name || 'Unknown',
                        email: recipient.email,
                        useInitialWhenNotSequential: true,
                        extra: recipient.type ? (
                          <Badge bg="light" text="dark" className="mt-1">
                            {recipient.type === 'signee' ? 'Signer' : 'CC'}
                          </Badge>
                        ) : null,
                      })}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted mb-0">No recipients added yet</p>
              )}
            </Card.Body>
          </Card>
        </div>

        <div className="col-md-6 mb-4">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0">
                <i className="ri-pencil-line me-2"></i>
                Fields
              </h6>
              {envelope.fields && envelope.fields.length > 0 ? (
                <Badge bg="success">
                  <i className="ri-check-line me-1"></i>
                  {envelope.fields.length} {envelope.fields.length === 1 ? 'Field' : 'Fields'}
                </Badge>
              ) : (
                <Badge bg="secondary">Not Added</Badge>
              )}
            </Card.Header>
            <Card.Body>
              {envelope.fields && envelope.fields.length > 0 ? (
                <div className="small">
                  <p className="mb-2">
                    <strong>Total Fields:</strong> {envelope.fields.length}
                  </p>
                  <div className="mb-3">
                    <strong>By Type:</strong>
                    {fieldTypeBreakdown.length > 0 ? (
                      <ul className="list-unstyled mb-0 mt-1">
                        {fieldTypeBreakdown.map(([type, count]) => (
                          <li key={type}>
                            <span className="text-capitalize">{type}</span>: {count}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-muted mb-0 mt-1">No type details available</p>
                    )}
                  </div>
                  <div>
                    <strong>By Recipient:</strong>
                    {fieldRecipientBreakdown.length > 0 ? (
                      <ul className="list-unstyled mb-0 mt-1">
                        {fieldRecipientBreakdown.map((item) => (
                          <li key={item.key} className="mb-1">
                            <div className="fw-semibold">{item.name} - {item.count}</div>
                            {item.email && (
                              <div className="text-muted">{item.email}</div>
                            )}
                            {!item.email && item.placeholder && (
                              <div className="text-muted">Placeholder: {item.placeholder}</div>
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-muted mb-0 mt-1">No recipient details available</p>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-muted mb-0">No fields placed yet</p>
              )}
            </Card.Body>
          </Card>
        </div>

        <div className="col-md-6 mb-4">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0">
                <i className="ri-mail-line me-2"></i>
                Message & Settings
              </h6>
              {(envelope.subject || envelope.message) ? (
                <Badge bg="success">
                  <i className="ri-check-line me-1"></i>
                  Configured
                </Badge>
              ) : (
                <Badge bg="secondary">Not Configured</Badge>
              )}
            </Card.Header>
            <Card.Body>
              {envelope.subject && (
                <div className="mb-2">
                  <strong>Subject:</strong>
                  <div className="text-muted">{envelope.subject}</div>
                </div>
              )}
              {envelope.message && (
                <div className="mb-2">
                  <strong>Message:</strong>
                  <div className="text-muted">{envelope.message}</div>
                </div>
              )}
              {envelope.reminder_frequency && (
                <div className="mb-2">
                  <strong>Reminder:</strong>
                  <Badge bg="light" text="dark">{envelope.reminder_frequency}</Badge>
                </div>
              )}
              {envelope.workflow_type && (
                <div>
                  <strong>Workflow:</strong>
                  <Badge bg="light" text="dark" className="ms-2">
                    {envelope.workflow_type === 'sequential' ? 'Sequential' : 'Parallel'}
                  </Badge>
                </div>
              )}
              {!envelope.subject && !envelope.message && (
                <p className="text-muted mb-0">No message configured yet</p>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="d-flex gap-2 justify-content-end mt-4">
        <Button variant="outline-secondary" onClick={() => navigate(isRevoked ? '/envelopes/revoked' : '/envelopes/drafts')}>
          <i className="ri-arrow-left-line me-2"></i>
          {isRevoked ? 'Back to Revoked' : 'Back to Drafts'}
        </Button>
        {!isRevoked && (
          <Button variant="primary" onClick={handleEdit}>
            <i className="ri-edit-line me-2"></i>
            Continue Editing
          </Button>
        )}
        {isRevoked && (
          <Button variant="primary" onClick={handleCopyEnvelope} disabled={copyLoading}>
            {copyLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Copying...
              </>
            ) : (
              <>
                <i className="ri-file-copy-line me-2"></i>
                Copy Envelope
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

export default EnvelopeProgress;
